import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { History, Location } from "history";
import { match } from "react-router-dom";

import { FormattedMessage } from "react-intl";
import { translate } from "@/modules/shared/IntlProviderWrapper";

import Loader from "@/modules/shared/ui/Loader";

import img_banner from "@/img/banner.png";
import CategoryItemsList from "./CategoryItemsList";
import CategoryCreateEditItemModal from "./CategoryCreateEditItemModal";
import ConfirmationModal from "@/modules/shared/ui/ConfirmationModal";
import { getBackend } from "@/lib/backend/backend2";
import type { ICategoryItem, ICategory } from "@/lib/backend/manufacturing2.generated.types";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { deepEqual } from "@/lib/util";

import "./CategoryCreate.scss";

// Extend ICategoryItem to include local UI state
export type ICategoryItemExt = ICategoryItem & {
    deleted?: boolean;
};

// Extend ICategory to use the extended ICategoryItem
interface ICategoryExt extends ICategory {
    items: ICategoryItemExt[];
}

type Props = {
    match: match<{ id: string }>;
    history: History;
    location: Location<{ categories_codes: string[] }>;
};

type ValidationErrors = {
    code?: string;
    name?: string;
};

/**
 * Form to Create category.
 */
const CategoryCreate = (props: Props) => {
    const isMountedRef = useRef(true);
    const [category, setCategory] = useState<ICategoryExt>({
        code: "",
        name: "",
        items: [],
        id: -1,
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>("");
    const [modalError, setModalError] = useState<string>("");
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [showCreateEditItemModal, setShowCreateEditItemModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ICategoryItemExt | null>(null);
    const [originalCategory, setOriginalCategory] = useState<ICategoryExt | null>(null);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [categoriesCodes, setCategoriesCodes] = useState<string[]>([]);

    useEffect(() => {
        if (props.location.state?.categories_codes) {
            setCategoriesCodes(props.location.state.categories_codes);
        }
    }, [props.location.state?.categories_codes]);

    const is_edit_mode = useMemo(() => Boolean(category.id && category.id > 0), [category.id]);

    // Check if there are unsaved changes using deep equality comparison
    const hasUnsavedChanges = useMemo(() => {
        if (!originalCategory) return false;

        // Create normalized versions for comparison (exclude UI-only properties)
        const normalizeCategory = (cat: ICategoryExt): Omit<ICategoryExt, "id"> => ({
            code: cat.code,
            name: cat.name,
            items: cat.items
                .filter(item => !item.deleted) // Only compare non-deleted items
                .map(item => ({
                    id: item.id,
                    code: item.code,
                    name: item.name,
                    description: item.description,
                })),
        });

        const current_normalized = normalizeCategory(category);
        const original_normalized = normalizeCategory(originalCategory);

        // Check if there are any deleted items (this indicates changes even if normalized versions are equal)
        const has_deleted_items = category.items.some(item => item.deleted);

        return has_deleted_items || !deepEqual(current_normalized, original_normalized);
    }, [category, originalCategory]);

    // Update isDirty flag when hasUnsavedChanges changes
    useEffect(() => {
        setIsDirty(hasUnsavedChanges);
    }, [hasUnsavedChanges]);

    const validateForm = useCallback((): ValidationErrors => {
        const errors: ValidationErrors = {};

        if (!category.code || category.code.trim() === "") {
            errors.code = translate("common.required_field", "This field is required");
        }

        if (!category.name || category.name.trim() === "") {
            errors.name = translate("common.required_field", "This field is required");
        }

        return errors;
    }, [category.code, category.name]);

    const handleSaveCategory = useCallback(async () => {
        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        if (categoriesCodes.length > 0 && !is_edit_mode) {
            const exists = categoriesCodes.some(code => code === category.code);

            if (exists) {
                setError(
                    translate("Categories.category_with_code_already_exists", "Category with code already exists")
                );
                return;
            }
        }

        setSaving(true);
        setError("");
        setModalError("");

        try {
            let id: number | undefined;
            // Filter out deleted items before saving
            const active_items = category.items.filter(item => !item.deleted);

            if (is_edit_mode) {
                // Convert to backend compatible format (exclude local UI properties)
                const backend_category: ICategory = {
                    ...category,
                    items: active_items.map(item => ({
                        code: item.code,
                        description: item.description,
                        name: item.name,
                        id: item.id,
                    })),
                };
                const res = await getBackend().manufacturing.updateCategory({
                    category_id: category.id,
                    category: backend_category,
                });
                id = res.category.id;
            } else {
                const category_items_without_id = active_items.map(item => ({
                    code: item.code,
                    description: item.description,
                    name: item.name,
                }));
                const res = await getBackend().manufacturing.insertCategory({
                    category: {
                        code: category.code,
                        name: category.name,
                        items: category_items_without_id,
                    },
                });
                id = res.category.id;
            }
            // Reset original category to current state after successful save
            if (isMountedRef.current) {
                setOriginalCategory({ ...category, items: active_items });
                setIsDirty(false);
            }
            props.history.push(`/digital-twin/resources/categories/${id}`);
        } catch (err) {
            if (isMountedRef.current) {
                setError(translate("common.error_save", "An error occurred while saving data"));
            }
        } finally {
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    }, [category, validateForm, is_edit_mode, props.history, categoriesCodes]);

    useEffect(() => {
        const fetchCategory = async () => {
            if (!isMountedRef.current) return;
            setLoading(true);
            setError("");
            setModalError("");
            try {
                const { category: fetched_category } = await getBackend().manufacturing.getCategory({
                    category_id: Number(props.match.params.id),
                });
                // Convert to local format with deleted and is_new flag support
                const local_category = {
                    ...fetched_category,
                    items: fetched_category.items.map(item => ({ ...item, deleted: false })),
                };
                if (isMountedRef.current) {
                    setCategory(local_category);
                    // Store original state for comparison
                    setOriginalCategory(local_category);
                }
            } catch (err) {
                if (isMountedRef.current) {
                    setError(
                        (err as Error).message || translate("common.error", "An error occurred while fetching data")
                    );
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        };
        if (props.match.params.id) {
            fetchCategory();
        } else {
            // For new categories, set the initial empty state as original
            const initial_category = {
                code: "",
                name: "",
                items: [],
                id: -1,
            };
            setOriginalCategory(initial_category);
        }
    }, [props.match.params.id]);

    // Handle browser navigation (refresh, close, etc.)
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                const message = translate(
                    "common.unsaved_changes_warning",
                    "You have unsaved changes. If you continue, they will be lost."
                );
                e.preventDefault();
                e.returnValue = message;
                return message;
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    // Cleanup effect to track component mount status
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleCancel = useCallback(() => {
        if (isDirty) {
            setShowUnsavedChangesModal(true);
            return;
        }
        props.history.goBack();
    }, [props.history, isDirty]);

    const handleConfirmLeave = useCallback(() => {
        setShowUnsavedChangesModal(false);
        props.history.goBack();
    }, [props.history]);

    const handleCancelLeave = useCallback(() => {
        setShowUnsavedChangesModal(false);
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            setCategory(prev => ({ ...prev, [name]: value }));

            if (validationErrors[name]) {
                const updatedErrors: ValidationErrors = { ...validationErrors };
                delete updatedErrors[name];
                setValidationErrors(updatedErrors);
            }
        },
        [validationErrors]
    );

    const checkCodeExists = useCallback(
        (code: string, excludeIndex?: number) => {
            return category.items.some(
                (item, idx) => item.code === code && (excludeIndex === undefined || idx !== excludeIndex)
            );
        },
        [category.items]
    );

    const addNewItem = useCallback(
        (item: ICategoryItemExt) => {
            if (checkCodeExists(item.code)) {
                setModalError(
                    translate(
                        "Categories.category_item_with_code_already_exists",
                        "Category item with code already exists"
                    )
                );
                return false;
            }
            setCategory(prev => ({ ...prev, items: [...prev.items, item] }));
            return true;
        },
        [checkCodeExists]
    );

    const updateExistingItem = useCallback(
        (item: ICategoryItemExt, index: number) => {
            if (item.id === null && checkCodeExists(item.code, index)) {
                setModalError(
                    translate(
                        "Categories.category_item_with_code_already_exists",
                        "Category item with code already exists"
                    )
                );
                return false;
            }
            setCategory(prev => {
                const new_items = [...prev.items];
                new_items[index] = { ...item };
                return { ...prev, items: new_items };
            });
            return true;
        },
        [checkCodeExists]
    );

    const handleSaveItem = useCallback(
        async (item: ICategoryItemExt, index: number) => {
            if (selectedItem === null) return;

            let success = false;

            if (index === -1) {
                // Adding new item
                success = addNewItem(item);
            } else {
                // Updating existing item (new or persisted)
                success = updateExistingItem(item, index);
            }

            if (success) {
                handleHideModal();
            }
        },
        [selectedItem, addNewItem, updateExistingItem]
    );

    const handleAddItem = useCallback(() => {
        setSelectedItem({
            name: "",
            code: "",
            description: "",
            id: null,
        });
        setShowCreateEditItemModal(true);
    }, []);

    const handleEditItem = useCallback((item: ICategoryItemExt) => {
        setSelectedItem(item);
        setShowCreateEditItemModal(true);
    }, []);

    const updateItemDeleted = useCallback((item: ICategoryItemExt, deleted: boolean) => {
        setCategory(prev => ({
            ...prev,
            items: prev.items.map(i =>
                (item.id === null ? i.code === item.code : i.id === item.id) ? { ...i, deleted } : i
            ),
        }));
    }, []);

    const handleMarkItemDeleted = useCallback(
        async (item: ICategoryItemExt) => {
            // Mark item as deleted in local state instead of calling backend
            updateItemDeleted(item, true);
        },
        [updateItemDeleted]
    );

    const handleUndoMarkItemDeleted = useCallback(
        (item: ICategoryItemExt) => {
            // Remove the deleted flag to restore the item
            updateItemDeleted(item, false);
        },
        [updateItemDeleted]
    );

    const handleHideModal = useCallback(() => {
        setShowCreateEditItemModal(false);
        setSelectedItem(null);
        setError("");
        setModalError("");
    }, []);

    const index = useMemo(() => {
        if (!selectedItem) {
            return -1;
        }
        return category.items.findIndex(item => item.id === selectedItem.id && item.code === selectedItem.code);
    }, [selectedItem?.id, category.items]);

    return (
        <div className="category-create">
            <section>
                <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
            </section>
            <section id="profile_edit" className="line-create new_line">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="white_box">
                                <ErrorComponent msg={error} type="error" />
                                <h2 className="mb-4">
                                    {is_edit_mode ? (
                                        <FormattedMessage
                                            id="Categories.edit_category"
                                            defaultMessage="Edit category"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="Categories.create_category"
                                            defaultMessage="Create category"
                                        />
                                    )}
                                </h2>
                                <div className="space30"></div>

                                {loading ? (
                                    <Loader />
                                ) : (
                                    <div className="tab-content">
                                        <form
                                            onSubmit={e => {
                                                e.preventDefault();
                                                handleSaveCategory();
                                            }}
                                        >
                                            <div className="form-group">
                                                <label htmlFor="category-code">
                                                    <FormattedMessage id="common.code" defaultMessage="Code" />
                                                    <span className="text-danger ml-1">*</span>
                                                </label>
                                                <input
                                                    id="category-code"
                                                    name="code"
                                                    type="text"
                                                    className={`form-control ${validationErrors.code ? "is-invalid" : ""}`}
                                                    value={category.code}
                                                    onChange={handleInputChange}
                                                    disabled={is_edit_mode || saving}
                                                    required
                                                    maxLength={50}
                                                    autoComplete="off"
                                                    autoFocus={!is_edit_mode}
                                                    aria-describedby="category-code-error"
                                                    aria-invalid={!!validationErrors.code}
                                                />
                                                {validationErrors.code && (
                                                    <div id="category-code-error" className="invalid-feedback">
                                                        {validationErrors.code}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="category-name">
                                                    <FormattedMessage id="common.name" defaultMessage="Name" />
                                                    <span className="text-danger ml-1">*</span>
                                                </label>
                                                <input
                                                    id="category-name"
                                                    name="name"
                                                    type="text"
                                                    className={`form-control ${validationErrors.name ? "is-invalid" : ""}`}
                                                    value={category.name}
                                                    onChange={handleInputChange}
                                                    disabled={saving}
                                                    required
                                                    maxLength={100}
                                                    autoComplete="off"
                                                    autoFocus={is_edit_mode}
                                                    aria-describedby="category-name-error"
                                                    aria-invalid={!!validationErrors.name}
                                                />
                                                {validationErrors.name && (
                                                    <div id="category-name-error" className="invalid-feedback">
                                                        {validationErrors.name}
                                                    </div>
                                                )}
                                            </div>
                                        </form>
                                        <div className="space30"></div>
                                        <CategoryItemsList
                                            items={category.items}
                                            canEdit={true}
                                            isLoading={loading}
                                            onAdd={handleAddItem}
                                            onEdit={handleEditItem}
                                            onRemove={handleMarkItemDeleted}
                                            onUndoDelete={handleUndoMarkItemDeleted}
                                        />
                                    </div>
                                )}
                                <div className="space30"></div>
                                <div className="button-list button-list-entity-edit d-flex justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={handleCancel}
                                        disabled={saving}
                                        aria-label={translate("common.cancel_and_go_back", "Cancel and go back")}
                                    >
                                        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSaveCategory}
                                        disabled={saving || loading}
                                        aria-label={
                                            is_edit_mode
                                                ? translate("Categories.update_category", "Update category")
                                                : translate("Categories.create_category", "Create category")
                                        }
                                    >
                                        <FormattedMessage id="common.save" defaultMessage="Save" />{" "}
                                        {saving && <i className="fas fa-spinner fa-spin ml-1" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space30"></div>
                        </div>
                    </div>
                </div>
            </section>
            <CategoryCreateEditItemModal
                show={showCreateEditItemModal}
                onHide={handleHideModal}
                category_item={selectedItem}
                onSave={handleSaveItem}
                isLoading={saving}
                error={modalError}
                index={index}
            />
            <ConfirmationModal
                show={showUnsavedChangesModal}
                title={translate("common.unsaved_change", "Unsaved change")}
                accept_label={translate("common.leave", "Leave")}
                cancel_label={translate("common.stay", "Stay")}
                dangerous={true}
                onClose={handleCancelLeave}
                onAccept={handleConfirmLeave}
                onCancel={handleCancelLeave}
            >
                <div>
                    <p>
                        {translate(
                            "common.unsaved_changes_warning",
                            "You have unsaved changes. If you continue, they will be lost."
                        )}
                    </p>
                    <p>{translate("common.are_you_sure2", "Are you sure you want to continue?")}</p>
                </div>
            </ConfirmationModal>
        </div>
    );
};

export default CategoryCreate;
